import { Field, FieldTypes, Form } from ".";
import _ from "lodash";
import * as moment from "moment";
import { FormSubmission, FormSubmissionOptionAnswer } from "./FormSubmission";
import { IComponentWithValidatedForm } from "../../components/validation";
import { FormFieldOptionData, FormSubmissionData } from "../../forms/formBuilder";
import { Utility } from "../../utils";
import { FieldValidationError, IFieldValidationError } from "../validation";

export interface FieldValueDisplay {
    isHtml: boolean;
    value: string;
}

export class FormSubmissionHelper {
    public static flattenFormModelFields(formModel: Form) {
        let fields: Field[] = [];
        for (const s of formModel.sections)
            fields = fields.concat(s.fields);
        return fields;
    }

    public static getFieldValueDisplay(field: Field, value: any): FieldValueDisplay {
        const display: FieldValueDisplay = { isHtml: false, value: "<em>None</em>" };
        if (value === null || value === undefined || value === "") {
            display.isHtml = true;
            return display;
        }

        if (FieldTypes.isTextType(field)) {
            display.isHtml = field.typeId === FieldTypes.RichText.id;
            display.value = value;
        }
        else if (field.typeId === FieldTypes.Date.id)
            display.value = moment(Utility.fromApiDate(value)).format("MMM D, YYYY");
        else if (field.typeId === FieldTypes.DateTime.id)
            display.value = moment(Utility.fromApiDate(value)).format("MMM D, YYYY h:mma");
        else if (field.typeId === FieldTypes.EmailAddress.id) {
            display.isHtml = true;
            display.value = `<a href="mailto:${value}">${value}</a>`;
        }
        else if (field.typeId === FieldTypes.Integer.id)
            display.value = parseInt(value, 10).toFixed(0);
        else if (field.typeId === FieldTypes.Number.id)
            display.value = parseFloat(value).toString();
        else if (field.typeId === FieldTypes.Number.id)
            display.value = parseFloat(value).toString();
        else if (FieldTypes.isTimeSpanType(field)) {
            display.value = value;
        }
        else if (FieldTypes.isListType(field)) {
            const selectedOpts = Array.isArray(value) ? (value as FormSubmissionOptionAnswer[]) : [];
            if (selectedOpts.length === 1) {
                const opt = _.find((field.options || []), (x) => x.optionId === selectedOpts[0].id);
                if (opt) {
                    display.value = opt.label + (opt.isOpenAnswer && selectedOpts[0].openAnswer ? `: ${selectedOpts[0].openAnswer}` : "");
                    display.isHtml = true;
                }
            }
            else {
                const opts = selectedOpts.map((s) => {
                    const opt = _.find((field.options || []), (x) => x.optionId === s.id);
                    if (opt)
                        return opt.label + (opt.isOpenAnswer && s.openAnswer ? `: ${s.openAnswer}` : "");
                    else
                        return "";
                });

                display.isHtml = true;
                display.value = `<ul class="options-v">${opts.join("")}</ul>`;
            }
        }
        return display;
    }

    public static getFieldName(inputPrefix: string, sectionIndex: number, fieldIndex: number) {
        return `${inputPrefix}sections[${sectionIndex}].fields[${fieldIndex}].value`;
    }

    public static getFieldOptionName(inputPrefix: string, sectionIndex: number, fieldIndex: number, optionIndex: number) {
        return `${inputPrefix}sections[${sectionIndex}].fields[${fieldIndex}].options[${optionIndex}]`;
    }

    public static getPostBody(formData: FormSubmissionData) {
        const fields: object[] = [];
        formData.sections.map((s) => {
            s.fields.map((f) => {
                fields.push({
                    fieldId: f.fieldId,
                    typeId: f.typeId,
                    value: FieldTypes.isListType(f)
                        ? (f.options || [])
                            .filter((opt) => opt.selected)
                            .map((opt) => ({
                                id: opt.optionId,
                                openAnswer: opt.openAnswer,
                            }))
                        : f.value,
                });
            });
        });

        const body = {
            fields,
            formId: formData.formId,
            submissionId: formData.submissionId || 0,
        };

        return body;
    }

    public static setFormData(formData: FormSubmissionData, formModel: Form, formSubmission?: FormSubmission) {
        formData.formId = formModel.formId;
        formData.submissionId = formSubmission ? formSubmission.submissionId : 0;
        formData.sections = formModel.sections.map((s, i) => ({
            description: s.description,
            fields: s.fields.map((f, j) => {
                const answer = formSubmission && _.find(formSubmission.answers, (x) => x.fieldId === f.fieldId);

                return {
                    css: f.metaData.cssClass,
                    description: f.metaData.description,
                    fieldId: f.fieldId,
                    hint: f.metaData.hint,
                    index: j,
                    isWordsRange: f.metaData.isWordsRange,
                    label: f.metaData.label,
                    max: f.metaData.max,
                    min: f.metaData.min,
                    options: (f.options || []).map((opt, k) => {
                        const answerOpt = answer && answer.answer && Array.isArray(answer.answer)
                            ? _.find((answer.answer as FormSubmissionOptionAnswer[]), (x) => x.id === opt.optionId)
                            : undefined;

                        return {
                            description: opt.description || "",
                            index: k,
                            isOpenAnswer: opt.isOpenAnswer,
                            label: opt.label || "",
                            openAnswer: answerOpt ? (answerOpt.openAnswer || "") : "",
                            openAnswerHint: opt.openAnswerHint,
                            openAnswerPlaceholder: opt.openAnswerPlaceholder,
                            optionId: opt.optionId,
                            selected: answerOpt !== undefined,
                        };
                    }),
                    placeholder: f.metaData.placeholder,
                    required: f.metaData.isRequired,
                    typeId: f.typeId,
                    value: answer ? answer.answer : null,
                };
            }),
            index: i,
            title: s.title,
        }));
    }

    public static validateSubmission(formComponent: IComponentWithValidatedForm, form: Form, formData: FormSubmissionData, inputPrefix: string = "") {
        const fieldModels = FormSubmissionHelper.flattenFormModelFields(form);
        const fieldErrors: IFieldValidationError[] = [];
        for (const sectionData of formData.sections) {
            for (const fieldData of sectionData.fields) {
                const fieldName = FormSubmissionHelper.getFieldName(inputPrefix, sectionData.index, fieldData.index);
                const field = _.find(fieldModels, (x) => x.fieldId === fieldData.fieldId);
                if (!field)
                    fieldErrors.push(new FieldValidationError(fieldName, `Form field not found for ID ${fieldData.fieldId}`));
                else {
                    let errors: string[] = [];
                    if (FieldTypes.isTextType(field))
                        errors = FormSubmissionHelper.validateString(field, fieldData.value);
                    else if (field.typeId === FieldTypes.Integer.id)
                        errors = FormSubmissionHelper.validateNumber(field, fieldData.value, true);
                    else if (field.typeId === FieldTypes.Number.id)
                        errors = FormSubmissionHelper.validateNumber(field, fieldData.value, false);
                    else if (FieldTypes.isListType(field)) {
                        // validation of list fields (i.e. radio, check, and dropdown lists)
                        const selectedOptions = (fieldData.options || []).filter((opt) => opt.selected);
                        if (field.metaData.isRequired && !selectedOptions.length)
                            errors.push(`${field.metaData.label} is a required field`);
                        for (const opt of selectedOptions) {
                            if (opt.isOpenAnswer && !opt.openAnswer) {
                                const optionName = FormSubmissionHelper.getFieldOptionName(inputPrefix, sectionData.index, fieldData.index, opt.index);
                                fieldErrors.push(new FieldValidationError(`${optionName}.openAnswer`,
                                    `${field.metaData.label} requires an open answer for the selected option.`));
                            }
                        }
                    }

                    if (errors.length)
                        for (const e of errors)
                            fieldErrors.push(new FieldValidationError(fieldName, e));
                }
            }
        }

        return fieldErrors;
    }

    public static validateNumber(field: Field, value: any, asInteger: boolean) {
        const errors: string[] = [];
        // if (field.metaData.isRequired && (value === undefined || value === null))
        //     errors.push(`${field.metaData.label} is a required field`);
        // else if (typeof value !== "number")
        //     errors.push(`${field.metaData.label} must be a number`);
        if (asInteger) {
            const regex = new RegExp("\W");
            if (regex.test(value || ""))
                errors.push(`${field.metaData.label} must be a whole number`);
        }
        return errors;
    }

    public static validateString(field: Field, value: any) {
        const errors: string[] = [];
        // if (field.metaData.isRequired && !value)
        //     errors.push(`${field.metaData.label} is a required field`);
        // else if (typeof value !== "string")
        //     errors.push(`${field.metaData.label} must be a string value`);
        // else if (field.metaData.max && (value + "").length > field.metaData.max)
        //     errors.push(`${field.metaData.label} cannot exceed ${field.metaData.max} characters.`);
        return errors;
    }

    public static validateOptions(field: Field, options?: FormFieldOptionData[]) {
        const errors: string[] = [];
        const selectedOptions = (options || []).filter((opt) => opt.selected);
        if (field.metaData.isRequired && !selectedOptions.length)
            errors.push(`${field.metaData.label} is a required field`);
        for (const opt of selectedOptions)
            if (opt.isOpenAnswer && !opt.openAnswer)
                errors.push(`${field.metaData.label} requires an open answer for the selected option.`);
        return errors;
    }
}



// WEBPACK FOOTER //
// ./src/models/formBuilder/FormSubmissionHelper.ts